/**
 * GameBalanceWidget — отображение баланса игр.
 *
 * Поведение на Home (вариант 'full'):
 *  - Новичкам (ничего не сыграно, paid=0, free на максимуме) виджет скрыт —
 *    чтобы не пугать дефицитом ещё до первой игры.
 *  - После первой игры виджет появляется. Формулировка зависит от баланса:
 *    «🎁 Бесплатных игр осталось: N» — если играл только за счёт бесплатных
 *    «🎮 Игр осталось: N» — когда уже есть платные.
 *  - Кнопка «Купить» показывается только в VK и только при низком балансе (≤3).
 *
 * В ProfilePage используется отдельная карточка (не этот виджет) — там
 * баланс и кнопка «Купить» видны всегда.
 */
import { useEffect } from 'react';
import { useGameBalance } from '../hooks/useGameBalance';
import { isVK } from '../platform/detect';
import { usePurchaseStore } from '../stores/purchase';

interface Props {
  variant?: 'compact' | 'full';
}

export function GameBalanceWidget({ variant = 'full' }: Props) {
  const { balance, loading, refetch } = useGameBalance();
  const { open, setRefetchBalance } = usePurchaseStore();

  useEffect(() => {
    setRefetchBalance(refetch);
    return () => setRefetchBalance(null);
  }, [refetch, setRefetchBalance]);

  if (loading || !balance) {
    return null;
  }

  const total = balance.paidGames + balance.freeGamesLeft;

  // Новичок: ничего не сыграно — баланс полный, платных нет.
  // Прячем виджет, чтобы не намекать на дефицит до первой игры.
  const isNewbie = balance.paidGames === 0
    && balance.freeGamesLeft === balance.totalFreeGames;

  // Кнопка «Купить» — только при низком балансе, чтобы не давить.
  const LOW_BALANCE_THRESHOLD = 3;
  const showBuy = isVK() && total <= LOW_BALANCE_THRESHOLD;

  // Формулировка: «🎁 Бесплатных игр осталось» если ещё не покупал,
  // обычная «🎮 Игр осталось» когда есть платные.
  const onlyFree = balance.paidGames === 0;
  const title = onlyFree
    ? `🎁 Бесплатных игр осталось: ${balance.freeGamesLeft}`
    : `🎮 Игр осталось: ${total}`;

  if (variant === 'compact') {
    if (isNewbie) return null;
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-white/80">🎮 {total}</span>
        {showBuy && (
          <button
            onClick={open}
            className="text-amber-300 hover:text-amber-200 underline text-xs"
          >
            Купить
          </button>
        )}
      </div>
    );
  }

  if (isNewbie) {
    return null;
  }

  return (
    <div className="bg-white/10 border border-white/20 rounded-2xl p-4 flex items-center justify-between">
      <div>
        <div className="text-white font-bold text-lg">{title}</div>
        {!onlyFree && (
          <div className="text-purple-300 text-xs mt-1">
            Бесплатных: {balance.freeGamesLeft} • Платных: {balance.paidGames}
          </div>
        )}
      </div>
      {showBuy && (
        <button
          onClick={open}
          className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold rounded-xl shadow-lg hover:scale-[1.03] transition-all active:scale-95"
        >
          Купить
        </button>
      )}
    </div>
  );
}
